/**
 * What the server's assistant offers (enabled, voice, speech to text, transcripts). It does
 * not change while the server runs, so it is fetched once a minute at most and shared: the
 * lobby asks while the room code is typed and the room finds the answer waiting, so Luma's
 * welcome no longer waits for it (2026-10-06).
 */
import { api, getJson } from '../lib/api.ts';
import type { AssistantInfo } from './types.ts';

const MAX_AGE_MS = 60_000;
let held: { at: number; info: Promise<AssistantInfo> } | undefined;

export function assistantInfo(): Promise<AssistantInfo> {
  if (!held || performance.now() - held.at > MAX_AGE_MS) {
    const info = retry(() => getJson<AssistantInfo>(api('assistant')), 4);
    info.catch(() => {
      if (held?.info === info) held = undefined;
    });
    held = { at: performance.now(), info };
  }
  return held.info;
}

/** Tries again (after 1, 2, 4 s) before giving up: the room's host must not stay mute. */
async function retry<T>(job: () => Promise<T>, attempts: number): Promise<T> {
  for (let i = 1; ; i++) {
    try {
      return await job();
    } catch (err) {
      if (i >= attempts) throw err;
      await new Promise((r) => setTimeout(r, 1000 * 2 ** (i - 1)));
    }
  }
}
